import { useEffect, useState } from 'react';
import Modal from './Modal';
import StringListInput from './StringListInput';
import { spinnerRing } from '../utils/bookingUtils';
import { validatePlan } from '../utils/posSubscriptionUtil';
import {
  POS_PLAN_DURATION_UNITS,
  POS_PLAN_DURATION_MAX,
  POS_PLAN_NAME_MAX_LENGTH,
  POS_PLAN_DESCRIPTION_MAX_LENGTH,
  POS_PLAN_FEATURE_MAX_LENGTH,
  POS_PLAN_BUTTON_LABEL_MAX_LENGTH,
  POS_PLAN_FINE_PRINT_MAX_LENGTH,
  POS_PLAN_STAFF_NOTE_MAX_LENGTH,
  POS_PLAN_FREE_STAFF_MAX,
} from '../constants/posConst';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/** A blank plan, for "Add plan". 30 days is the common case. */
const BLANK_PLAN = {
  name: '',
  isTrial: false,
  price: '',
  durationValue: 30,
  durationUnit: 'days',
  description: '',
  features: [],
  recommended: false,
  visibleToClients: true,
  sortOrder: 0,
  // Landing-page pricing card (see PosLandingPricing).
  isCustom: false,
  compareAtPrice: '',
  freeStaff: 0,
  staffNote: '',
  allowAddons: true,
  buttonLabel: '',
  finePrint: '',
};

/**
 * Creates or edits one subscription plan.
 *
 * Built on the shared Modal, which carries a × and does not close on a
 * backdrop tap — a half-typed plan lost to a stray tap is the loss that rule
 * exists to prevent.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {object|null} props.plan - the plan to edit, or null to add one
 * @param {(draft: object) => Promise<void>} props.onSave - writes the plan;
 *   a rejection is shown inside the dialog rather than closing it
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function PosPlanModal({ isOpen, plan = null, onSave, onClose }) {
  const [draft, setDraft] = useState(BLANK_PLAN);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Re-seeded every time the dialog opens, so editing one plan straight after
  // another never shows the previous plan's fields.
  useEffect(() => {
    if (!isOpen) return;
    setDraft(plan ? {
      ...BLANK_PLAN,
      ...plan,
      price: String(plan.price),
      // 0 is stored for "none"; the field shows blank for it.
      compareAtPrice: plan.compareAtPrice ? String(plan.compareAtPrice) : '',
    } : BLANK_PLAN);
    setError('');
    setSaving(false);
  }, [isOpen, plan]);

  /**
   * Returns a change handler for one field.
   *
   * @param {string} key
   * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void}
   */
  const field = (key) => (event) => {
    const { type, checked, value } = event.target;
    setDraft((current) => ({ ...current, [key]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  /**
   * Validates, then saves. Guarded so a double-click cannot create two plans.
   *
   * @returns {Promise<void>}
   */
  const handleConfirm = async () => {
    if (saving) return;
    try {
      validatePlan({ ...draft, durationValue: Number(draft.durationValue) });
    } catch (validationError) {
      setError(validationError.message);
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...draft, durationValue: Number(draft.durationValue) });
    } catch (saveError) {
      setError(saveError.message || 'The plan could not be saved. Please try again.');
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={plan ? 'Edit plan' : 'Add plan'}
      type="info"
      hideIcon
      showCancel
      confirmText={saving ? 'Saving…' : (plan ? 'Save plan' : 'Add plan')}
      onConfirm={handleConfirm}
    >
      <div className="pa-role-form ps-plan-form">
        <label className="pa-field">
          <span className="pa-field-label">Plan name</span>
          <input
            className="pa-input"
            value={draft.name}
            onChange={field('name')}
            placeholder="Monthly"
            maxLength={POS_PLAN_NAME_MAX_LENGTH}
            disabled={saving}
          />
        </label>

        <label className="pa-check">
          <input type="checkbox" checked={draft.isTrial} onChange={field('isTrial')} disabled={saving} />
          <span>
            <span className="pa-field-label">This is a free trial</span>
            <span className="pa-field-hint">
              Free, and granted by an administrator only — clients never see a trial in their
              Subscribe list, so it cannot be re-availed.
            </span>
          </span>
        </label>

        {!draft.isTrial && (
          <label className="pa-check">
            <input type="checkbox" checked={draft.isCustom} onChange={field('isCustom')} disabled={saving} />
            <span>
              <span className="pa-field-label">Custom pricing (Enterprise)</span>
              <span className="pa-field-hint">
                No price: the landing page shows &ldquo;Schedule a Strategy Session&rdquo; and books a demo
                call instead of taking payment. Never offered in a till&rsquo;s Subscribe list.
              </span>
            </span>
          </label>
        )}

        <div className="ps-form-row">
          {!draft.isTrial && !draft.isCustom && (
            <label className="pa-field">
              <span className="pa-field-label">Price</span>
              <input
                className="pa-input"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={draft.price}
                onChange={field('price')}
                placeholder="999"
                disabled={saving}
              />
            </label>
          )}
          <label className="pa-field">
            <span className="pa-field-label">Length</span>
            <input
              className="pa-input"
              type="number"
              min="1"
              max={POS_PLAN_DURATION_MAX}
              step="1"
              value={draft.durationValue}
              onChange={field('durationValue')}
              disabled={saving}
            />
          </label>
          <label className="pa-field">
            <span className="pa-field-label">Counted in</span>
            <select className="pa-input" value={draft.durationUnit} onChange={field('durationUnit')} disabled={saving}>
              {POS_PLAN_DURATION_UNITS.map((unit) => (
                <option key={unit.key} value={unit.key}>{unit.label}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="pa-field">
          <span className="pa-field-label">Short description</span>
          <textarea
            className="pa-input pa-textarea"
            rows={2}
            value={draft.description}
            onChange={field('description')}
            placeholder="Billed monthly. Cancel any time."
            maxLength={POS_PLAN_DESCRIPTION_MAX_LENGTH}
            disabled={saving}
          />
        </label>

        <div className="pa-field">
          <span className="pa-field-label">What&rsquo;s included</span>
          <StringListInput
            values={draft.features}
            onChange={(features) => { setDraft((current) => ({ ...current, features })); setError(''); }}
            placeholder="Unlimited products and sales"
            addLabel="Add inclusion"
            itemLabel="inclusion"
            disabled={saving}
          />
          <span className="pa-field-hint">One line each, up to {POS_PLAN_FEATURE_MAX_LENGTH} characters.</span>
        </div>

        <div className="ps-form-row">
          <label className="pa-field">
            <span className="pa-field-label">Display order</span>
            <input
              className="pa-input"
              type="number"
              step="1"
              value={draft.sortOrder}
              onChange={field('sortOrder')}
              disabled={saving}
            />
            <span className="pa-field-hint">Lower shows first.</span>
          </label>
        </div>

        {!draft.isTrial && (
          <fieldset className="ps-landing-fields">
            <legend className="pa-field-label">Landing page pricing card</legend>
            <span className="pa-field-hint">
              How this plan reads on the public page at /. The plan name is the toggle label and the
              short description is the line under the price.
            </span>

            {!draft.isCustom && (
              <div className="ps-form-row">
                <label className="pa-field">
                  <span className="pa-field-label">Compare-at price</span>
                  <input
                    className="pa-input"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={draft.compareAtPrice}
                    onChange={field('compareAtPrice')}
                    placeholder="1488"
                    disabled={saving}
                  />
                  <span className="pa-field-hint">Struck through beside the price. The saving and the % badge are worked out from it. Blank for none.</span>
                </label>
                <label className="pa-field">
                  <span className="pa-field-label">Free staff accounts</span>
                  <input
                    className="pa-input"
                    type="number"
                    min="0"
                    max={POS_PLAN_FREE_STAFF_MAX}
                    step="1"
                    value={draft.freeStaff}
                    onChange={field('freeStaff')}
                    disabled={saving}
                  />
                  <span className="pa-field-hint">Included with the plan, before any add-ons. When a client renews on this plan, this many staff accounts are free — only accounts beyond it are charged.</span>
                </label>
              </div>
            )}

            {!draft.isCustom && (
              <>
                <label className="pa-field">
                  <span className="pa-field-label">Staff add-on note</span>
                  <input
                    className="pa-input"
                    value={draft.staffNote}
                    onChange={field('staffNote')}
                    placeholder="cover shifts & track per staff"
                    maxLength={POS_PLAN_STAFF_NOTE_MAX_LENGTH}
                    disabled={saving}
                  />
                </label>
                <label className="pa-check">
                  <input type="checkbox" checked={draft.allowAddons} onChange={field('allowAddons')} disabled={saving} />
                  <span>
                    <span className="pa-field-label">Offer add-ons with this plan</span>
                    <span className="pa-field-hint">Extra staff accounts and the Warehouse / Stock Room. Prices are in Config → Admin Settings.</span>
                  </span>
                </label>
              </>
            )}

            <div className="ps-form-row">
              <label className="pa-field">
                <span className="pa-field-label">Button label</span>
                <input
                  className="pa-input"
                  value={draft.buttonLabel}
                  onChange={field('buttonLabel')}
                  placeholder={draft.isCustom ? 'Schedule a strategy call' : 'Subscribe'}
                  maxLength={POS_PLAN_BUTTON_LABEL_MAX_LENGTH}
                  disabled={saving}
                />
              </label>
            </div>

            <label className="pa-field">
              <span className="pa-field-label">Fine print</span>
              <textarea
                className="pa-input pa-textarea"
                rows={2}
                value={draft.finePrint}
                onChange={field('finePrint')}
                placeholder="Full premium access for 7 days trial. No card, no e-wallet, no auto-charge when it ends."
                maxLength={POS_PLAN_FINE_PRINT_MAX_LENGTH}
                disabled={saving}
              />
              <span className="pa-field-hint">The small line under the button.</span>
            </label>
          </fieldset>
        )}

        <label className="pa-check">
          <input type="checkbox" checked={draft.recommended} onChange={field('recommended')} disabled={saving} />
          <span>
            <span className="pa-field-label">Mark as recommended</span>
            <span className="pa-field-hint">Highlighted in the client&rsquo;s plan list.</span>
          </span>
        </label>

        <label className="pa-check">
          <input type="checkbox" checked={draft.visibleToClients} onChange={field('visibleToClients')} disabled={saving} />
          <span>
            <span className="pa-field-label">Offer this plan to clients</span>
            <span className="pa-field-hint">
              Untick to retire a plan without deleting it. Clients already on it keep their dates.
            </span>
          </span>
        </label>

        {saving && <div className="pa-loading pa-loading-inline">{spinnerRing('Saving plan')}</div>}
        {error && <p className="pa-role-error">{error}</p>}
      </div>
    </Modal>
  );
}

export default PosPlanModal;
